/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { Box, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import React from 'react'

import useT from '@/i18n/useT'
import { getRenderedBoxRect } from './classificationUtils'
import PanZoomImageViewer, {
  PanZoomImageViewerHandle,
  PanZoomImageViewerRenderContext,
  PanZoomPointerInfo,
} from './PanZoomImageViewer'
import {
  DetectionBox,
  DetectionClass,
  DetectionTagOption,
  getDetectionClassLabels,
  getDetectionClassColor,
} from './detectionUtils'

const MIN_BOX_SIZE = 0.008

type DetectionAnnotatorProps = {
  imageUrl: string
  boxes: DetectionBox[]
  editable?: boolean
  activeClass: DetectionClass
  activeTag: DetectionTagOption
  selectedBoxIds: string[]
  hoveredClass: DetectionClass | null
  hiddenClasses: Set<DetectionClass>
  onCreateBox: (
    bounds: Pick<DetectionBox, 'minX' | 'maxX' | 'minY' | 'maxY'>,
  ) => void
  onSelectedBoxIdsChange: (
    boxIds: string[] | ((current: string[]) => string[]),
  ) => void
  maxImageHeight?: string
  topControls?: React.ReactNode
  bottomLeftControls?: React.ReactNode
  bottomRightControls?: React.ReactNode
}

export type DetectionAnnotatorHandle = {
  resetView: () => void
}

type DraftBox = {
  startX: number
  startY: number
  endX: number
  endY: number
}

const getDraftRect = (draftBox: DraftBox) => ({
  minX: Math.min(draftBox.startX, draftBox.endX),
  maxX: Math.max(draftBox.startX, draftBox.endX),
  minY: Math.min(draftBox.startY, draftBox.endY),
  maxY: Math.max(draftBox.startY, draftBox.endY),
})

const isPanGesture = (pointerInfo: PanZoomPointerInfo) =>
  pointerInfo.isPanGesture

const DetectionAnnotator = React.forwardRef<
  DetectionAnnotatorHandle,
  DetectionAnnotatorProps
>(
  (
    {
      imageUrl,
      boxes,
      editable = true,
      activeClass,
      activeTag,
      selectedBoxIds,
      hoveredClass,
      hiddenClasses,
      onCreateBox,
      onSelectedBoxIdsChange,
      maxImageHeight = 'calc(100dvh - 240px)',
      topControls,
      bottomLeftControls,
      bottomRightControls,
    },
    ref,
  ) => {
    const { t } = useT()
    const detectionClassLabels = getDetectionClassLabels(t)
    const viewerRef = React.useRef<PanZoomImageViewerHandle | null>(null)
    const topControlsRef = React.useRef<HTMLDivElement | null>(null)
    const bottomControlsRef = React.useRef<HTMLDivElement | null>(null)
    const [naturalImageSize, setNaturalImageSize] = React.useState<{
      width: number
      height: number
    } | null>(null)
    const [topControlsHeight, setTopControlsHeight] = React.useState(0)
    const [bottomControlsHeight, setBottomControlsHeight] = React.useState(0)
    const [draftBox, setDraftBox] = React.useState<DraftBox | null>(null)
    const [hoveredBoxId, setHoveredBoxId] = React.useState<string | null>(null)
    const [mousePosition, setMousePosition] = React.useState<{
      x: number
      y: number
      visible: boolean
    }>({
      x: 0,
      y: 0,
      visible: false,
    })

    const activeColor = getDetectionClassColor(activeClass, activeTag.name)

    React.useImperativeHandle(ref, () => ({
      resetView: () => {
        viewerRef.current?.reset()
      },
    }))

    React.useEffect(() => {
      setNaturalImageSize(null)
      const img = new window.Image()
      img.onload = () =>
        setNaturalImageSize({
          width: img.naturalWidth,
          height: img.naturalHeight,
        })
      img.src = imageUrl
    }, [imageUrl])

    React.useEffect(() => {
      const observed = [
        {
          element: topControlsRef.current,
          setHeight: setTopControlsHeight,
        },
        {
          element: bottomControlsRef.current,
          setHeight: setBottomControlsHeight,
        },
      ]

      const updateHeights = () => {
        observed.forEach(({ element, setHeight }) => {
          setHeight(
            element ? Math.ceil(element.getBoundingClientRect().height) : 0,
          )
        })
      }

      updateHeights()
      if (typeof ResizeObserver === 'undefined') {
        return undefined
      }

      const observers = observed
        .filter(
          (
            item,
          ): item is {
            element: HTMLDivElement
            setHeight: React.Dispatch<React.SetStateAction<number>>
          } => item.element !== null,
        )
        .map(({ element }) => {
          const observer = new ResizeObserver(updateHeights)
          observer.observe(element)
          return observer
        })

      return () => {
        observers.forEach(observer => observer.disconnect())
      }
    }, [bottomLeftControls, bottomRightControls, topControls])

    const visibleBoxes = React.useMemo(
      () =>
        boxes.filter(box => {
          if (hiddenClasses.has(box.detectionClass)) return false
          if (hoveredClass && box.detectionClass !== hoveredClass) return false
          return true
        }),
      [boxes, hiddenClasses, hoveredClass],
    )

    const selectedBoxIdsSet = React.useMemo(
      () => new Set(selectedBoxIds),
      [selectedBoxIds],
    )
    const hoveredBox = boxes.find(box => box.id === hoveredBoxId) || null

    const handleMouseDown = (pointerInfo: PanZoomPointerInfo) => {
      if (isPanGesture(pointerInfo)) {
        setDraftBox(null)
        setMousePosition(current => ({ ...current, visible: false }))
        return
      }

      if (!editable) {
        setDraftBox(null)
        setMousePosition(current => ({ ...current, visible: false }))
        return
      }

      if (!pointerInfo.withinImage) {
        onSelectedBoxIdsChange([])
        return
      }

      setDraftBox({
        startX: pointerInfo.normalizedX,
        startY: pointerInfo.normalizedY,
        endX: pointerInfo.normalizedX,
        endY: pointerInfo.normalizedY,
      })
    }

    const handleMouseMove = (pointerInfo: PanZoomPointerInfo) => {
      const rect = (
        pointerInfo.event.currentTarget as HTMLDivElement
      ).getBoundingClientRect()
      if (isPanGesture(pointerInfo)) {
        setMousePosition({
          x: pointerInfo.event.clientX - rect.left,
          y: pointerInfo.event.clientY - rect.top,
          visible: false,
        })
        return
      }

      setMousePosition({
        x: pointerInfo.event.clientX - rect.left,
        y: pointerInfo.event.clientY - rect.top,
        visible: editable && pointerInfo.withinImage,
      })

      if (!draftBox) return

      setDraftBox(current =>
        current
          ? {
              ...current,
              endX: pointerInfo.normalizedX,
              endY: pointerInfo.normalizedY,
            }
          : current,
      )
    }

    const handleMouseUp = (pointerInfo: PanZoomPointerInfo) => {
      const clientRect = (
        pointerInfo.event.currentTarget as HTMLDivElement
      ).getBoundingClientRect()
      setMousePosition({
        x: pointerInfo.event.clientX - clientRect.left,
        y: pointerInfo.event.clientY - clientRect.top,
        visible: pointerInfo.withinImage,
      })

      if (isPanGesture(pointerInfo)) {
        setDraftBox(null)
        setMousePosition(current => ({ ...current, visible: false }))
        return
      }

      if (!editable) {
        setDraftBox(null)
        setMousePosition(current => ({ ...current, visible: false }))
        return
      }

      if (!draftBox) return

      const nextRect = getDraftRect({
        ...draftBox,
        endX: pointerInfo.normalizedX,
        endY: pointerInfo.normalizedY,
      })
      setDraftBox(null)

      if (
        nextRect.maxX - nextRect.minX < MIN_BOX_SIZE ||
        nextRect.maxY - nextRect.minY < MIN_BOX_SIZE
      ) {
        return
      }

      onCreateBox(nextRect)
    }

    const renderViewportOverlay = (
      context: PanZoomImageViewerRenderContext,
    ) => {
      const hoveredBoxLabel = hoveredBox
        ? hoveredBox.detectionClass === 'PRODUCT' && hoveredBox.tag
          ? `${detectionClassLabels[hoveredBox.detectionClass]} · ${hoveredBox.tag}`
          : detectionClassLabels[hoveredBox.detectionClass]
        : null

      let hoveredLabelPosition: { left: number; top: number } | null = null
      if (hoveredBox && context.naturalSize) {
        const rect = getRenderedBoxRect(
          hoveredBox,
          context.naturalSize,
          context.renderedSize,
        )
        const offsetLeft =
          context.containerSize.width / 2 -
          context.renderedSize.width / 2 +
          context.pan.x
        const offsetTop =
          context.containerSize.height / 2 -
          context.renderedSize.height / 2 +
          context.pan.y
        hoveredLabelPosition = {
          left: offsetLeft + rect.left,
          top: offsetTop + rect.top - 30,
        }
      }

      return (
        <>
          {editable && mousePosition.visible && (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  left: mousePosition.x,
                  top: 0,
                  bottom: 0,
                  width: '2px',
                  backgroundColor: activeColor,
                  opacity: 0.85,
                  pointerEvents: 'none',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: mousePosition.y,
                  left: 0,
                  right: 0,
                  height: '2px',
                  backgroundColor: activeColor,
                  opacity: 0.85,
                  pointerEvents: 'none',
                }}
              />
            </>
          )}
          {hoveredBoxLabel && hoveredLabelPosition && (
            <Box
              sx={{
                position: 'absolute',
                left: hoveredLabelPosition.left,
                top: hoveredLabelPosition.top,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: 'rgba(15, 23, 42, 0.86)',
                color: '#fff',
                pointerEvents: 'none',
              }}
            >
              <Typography variant="caption">{hoveredBoxLabel}</Typography>
            </Box>
          )}
        </>
      )
    }

    const renderImageOverlay = (context: PanZoomImageViewerRenderContext) => {
      if (!context.naturalSize) return null

      const draftRect = draftBox ? getDraftRect(draftBox) : null

      return (
        <>
          {visibleBoxes.map(box => {
            const rect = getRenderedBoxRect(
              box,
              context.naturalSize!,
              context.renderedSize,
            )
            const color =
              box.detectionClass === 'PRODUCT' && box.tag
                ? getDetectionClassColor('PRODUCT', box.tag)
                : getDetectionClassColor(box.detectionClass, activeTag.name)
            const selected = selectedBoxIdsSet.has(box.id)
            return (
              <Box
                key={box.id}
                onDragStart={event => event.preventDefault()}
                onMouseEnter={() => setHoveredBoxId(box.id)}
                onMouseLeave={() =>
                  setHoveredBoxId(current =>
                    current === box.id ? null : current,
                  )
                }
                onClick={event => {
                  if (!editable) {
                    return
                  }
                  event.stopPropagation()
                  onSelectedBoxIdsChange(current =>
                    current.includes(box.id)
                      ? current.filter(id => id !== box.id)
                      : [...current, box.id],
                  )
                }}
                sx={{
                  position: 'absolute',
                  left: rect.left,
                  top: rect.top,
                  width: rect.width,
                  height: rect.height,
                  border: `2px solid ${color}`,
                  backgroundColor: 'transparent',
                  boxShadow: selected
                    ? `inset 0 0 0 9999px ${alpha(color, 0.18)}`
                    : 'none',
                  pointerEvents: 'auto',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  cursor: editable ? 'pointer' : 'default',
                }}
              />
            )
          })}
          {draftRect && (
            <Box
              sx={{
                position: 'absolute',
                left: `${draftRect.minX * context.renderedSize.width}px`,
                top: `${draftRect.minY * context.renderedSize.height}px`,
                width: `${(draftRect.maxX - draftRect.minX) * context.renderedSize.width}px`,
                height: `${(draftRect.maxY - draftRect.minY) * context.renderedSize.height}px`,
                border: `2px dashed ${activeColor}`,
                pointerEvents: 'none',
              }}
            />
          )}
        </>
      )
    }

    const controlsGapPx = 12
    const controlsReserveHeight =
      topControlsHeight +
      bottomControlsHeight +
      (topControls ? controlsGapPx : 0) +
      (bottomLeftControls || bottomRightControls ? controlsGapPx : 0)
    const viewerMaxHeight = `calc(${maxImageHeight} - ${controlsReserveHeight}px)`

    const stage = naturalImageSize
      ? {
          aspectRatio: `${naturalImageSize.width} / ${naturalImageSize.height}`,
          maxWidth: `calc(${viewerMaxHeight} * ${naturalImageSize.width} / ${naturalImageSize.height})`,
          fitGutterPx: 0,
        }
      : {
          aspectRatio: '4 / 3',
          maxWidth: `calc(${viewerMaxHeight} * 4 / 3)`,
          fitGutterPx: 18,
        }

    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Stack
          spacing={1.5}
          sx={{
            width: '100%',
            maxWidth: stage.maxWidth,
          }}
        >
          {topControls ? (
            <Stack
              ref={topControlsRef}
              direction="row"
              justifyContent="flex-end"
            >
              {topControls}
            </Stack>
          ) : null}
          <Box
            sx={{
              width: '100%',
              aspectRatio: stage.aspectRatio,
              maxWidth: stage.maxWidth,
              maxHeight: viewerMaxHeight,
              minHeight: `min(220px, ${viewerMaxHeight})`,
            }}
          >
            <PanZoomImageViewer
              ref={viewerRef}
              imageUrl={imageUrl}
              height="100%"
              panMode="right"
              idleCursor={editable ? 'crosshair' : 'default'}
              renderImageOverlay={renderImageOverlay}
              renderViewportOverlay={renderViewportOverlay}
              onViewerMouseDown={handleMouseDown}
              onViewerMouseMove={handleMouseMove}
              onViewerMouseUp={handleMouseUp}
              fitGutterPx={stage.fitGutterPx}
              minZoom={0.35}
            />
          </Box>
          {(bottomLeftControls || bottomRightControls) && (
            <Stack
              ref={bottomControlsRef}
              direction="row"
              spacing={1.5}
              justifyContent="space-between"
              alignItems="flex-end"
              flexWrap="wrap"
              useFlexGap
              sx={{ minHeight: 40 }}
            >
              <Box>{bottomLeftControls}</Box>
              <Box sx={{ marginLeft: 'auto' }}>{bottomRightControls}</Box>
            </Stack>
          )}
        </Stack>
      </Box>
    )
  },
)

DetectionAnnotator.displayName = 'DetectionAnnotator'

export default DetectionAnnotator
